/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  ASYNC_AUTOCOMPLETE_TYPES,
  AUTOCOMPLETE_INPUT_CONDITION_TYPES,
  DataActionConditionType,
  getTypeIcon,
} from 'cio/utils/campaigns/data-action';
import { action } from '@ember/object';
import { Select } from 'ember-power-select/components/power-select';
import merge from 'lodash.merge';
import { isEmpty } from '@ember/utils';
import CampaignModel, { CampaignType } from 'cio/models/campaign';
import { SafeString } from 'handlebars';
import { task, timeout } from 'ember-concurrency';
import config from 'cio/config/environment';
import AttributeTypeLabelDropdownItem from 'cio/components/attribute-type-label-dropdown-item';

interface DataActionTypeValueEditorValueArgs {
  campaign: CampaignModel;
  value: string;
  onChange: (value: string) => unknown;
  type: DataActionConditionType;
  errors?: (string | SafeString)[];
  updateHighlight?: (highlight: Select['highlighted']) => unknown;
  onFocus?: () => unknown;
  onBlur?: () => unknown;
  placeholders?: Partial<Record<DataActionConditionType, string>>;
  autocompleteValues?: Partial<Record<DataActionConditionType, { label: string; value: string; path: string[] }[]>>;
  isLoadingAsyncAutocompleteValues?: boolean;
  hasReadOnlyPermission: boolean;
}

const LIQUID_CUSTOMER_PLACEHOLDER = 'Use Liquid, e.g. {{customer.value}}';
const LIQUID_TRIGGER_PLACEHOLDER = 'Use Liquid, e.g. {{trigger.value}}';
const JS_CUSTOMER_PLACEHOLDER = 'Use JavaScript, e.g. return customer.value;';
const JS_TRIGGER_PLACEHOLDER = 'Use JavaScript, e.g. return trigger.value;';

type DataActionConditionTypePlaceholders = Record<DataActionConditionType, string>;

const DEFAULT_PLACEHOLDERS: Partial<DataActionConditionTypePlaceholders> = {
  [DataActionConditionType.Static]: 'Use a static value',
  [DataActionConditionType.TriggerAttribute]: 'Use sample data',
  [DataActionConditionType.ProfileAttribute]: 'Use sample data',
  [DataActionConditionType.EventAttribute]: 'Use sample data',
  [DataActionConditionType.Liquid]: LIQUID_CUSTOMER_PLACEHOLDER,
  [DataActionConditionType.JavaScript]: JS_CUSTOMER_PLACEHOLDER,
};

type CampaignTypePlaceholderOverrides = Record<CampaignType, Partial<DataActionConditionTypePlaceholders>>;

const CAMPAIGN_TYPE_PLACEHOLDER_OVERRIDES: Partial<CampaignTypePlaceholderOverrides> = {
  [CampaignType.Webhook]: {
    [DataActionConditionType.Liquid]: LIQUID_TRIGGER_PLACEHOLDER,
    [DataActionConditionType.JavaScript]: JS_TRIGGER_PLACEHOLDER,
  },
};

export default class DataActionTypeValueEditorValue extends Component<DataActionTypeValueEditorValueArgs> {
  DataActionConditionType = DataActionConditionType;

  @tracked inputTouched = false;

  get shouldShowError() {
    return this.hasError && this.inputTouched;
  }

  get hasError() {
    return !isEmpty(this.args.errors);
  }

  get showLoadingSpinner() {
    return this.args.isLoadingAsyncAutocompleteValues && ASYNC_AUTOCOMPLETE_TYPES.includes(this.args.type);
  }

  get isAutocompleteInputConditionType() {
    return AUTOCOMPLETE_INPUT_CONDITION_TYPES.includes(this.args.type);
  }

  get autocompleteValues() {
    return this.args.autocompleteValues?.[this.args.type] ?? [];
  }

  get autocompleteInputItemComponent() {
    return this.args.type === DataActionConditionType.ProfileAttribute ? AttributeTypeLabelDropdownItem : null;
  }

  get placeholders() {
    return merge(
      {},
      DEFAULT_PLACEHOLDERS,
      CAMPAIGN_TYPE_PLACEHOLDER_OVERRIDES[this.args.campaign.type] ?? {},
      this.args.placeholders ?? {},
    );
  }

  get inputIcon() {
    return getTypeIcon(this.args.type);
  }

  get showInputIcon() {
    return this.inputIcon != null && !isEmpty(this.args.value);
  }

  @action
  registerAPI(select: Select) {
    if (!select.isOpen) {
      return;
    }

    this.updateHighlight(select.highlighted);
  }

  @action
  onFocus() {
    this.args.onFocus?.();
  }

  @action
  onBlur() {
    if (!this.inputTouched) {
      this.inputTouched = true;
    }

    this.args.onBlur?.();
  }

  @action
  onChange(value: string) {
    if (!this.inputTouched) {
      this.inputTouched = true;
    }

    this.args.onChange(value);
  }

  onInput = task({ restartable: true }, async (value: string) => {
    await timeout(config.APP.debounceDuration);

    this.onChange(value);
  });

  @action
  updateHighlight(highlight: Select['highlighted']) {
    if (this.args.updateHighlight == null) {
      return;
    }

    return this.args.updateHighlight(highlight);
  }
}
